import { PandaDocument, Application } from "@otw/models";
import { axiosClient } from "../axiosClient";

export const pandaApi = {
  shared: {
    getViewableDocument: async (applicationId: string): Promise<Blob> => {
      return await axiosClient.post(
        "/panda/shared/get-viewable-document",
        { applicationId },
        { responseType: "blob" }
      );
    },
    getApplicationDocuments: async (
      applicationId: string
    ): Promise<Record<string, string>> => {
      return await axiosClient.post("/panda/shared/get-application-documents", {
        applicationId,
      });
    },
  },

  bank: {},

  client: {
    generateApplication: async (): Promise<{ url: string }> => {
      return await axiosClient.get("/panda/client/generate-application");
    },
    submitApplication: async (
      pandaPayload: PandaDocument
    ): Promise<{ status: string }> => {
      return await axiosClient.post("/panda/client/submit-application", {
        pandaPayload,
      });
    },
  },

  admin: {
    sendApplicationBackForCorrections: async (
      applicationId: string,
      correctionNotes: string,
      resendOnly?: boolean
    ): Promise<{ status: string }> => {
      return await axiosClient.post(
        "/panda/admin/send-application-back-for-corrections",
        { applicationId, correctionNotes, resendOnly }
      );
    },
    assignApplicationToAgent: async (
      applicationId: string,
      bankAgentId: string,
      bankId: string
    ): Promise<{ status: string }> => {
      return await axiosClient.post("/panda/admin/assign-application-to-agent", {
        applicationId,
        bankAgentId,
        bankId,
      });
    },
    updateApplicationStatus: async (
      applicationId: string,
      status: string,
      notes?: string,
      correctionNotes?: string,
      bankDecisionNotes?: string,
      bankAgentId?: string
    ): Promise<{ status: string }> => {
      return await axiosClient.post("/panda/admin/update-application-status", {
        applicationId,
        status,
        notes,
        correctionNotes,
        bankDecisionNotes,
        bankAgentId,
      });
    },
    generateApplicationForUser: async (
      userId: string,
      bankId: string,
      useBankSpecificProfile?: boolean
    ): Promise<{ url: string }> => {
      return await axiosClient.post(
        "/panda/admin/generate-application-for-user",
        {
          userId,
          bankId,
          useBankSpecificProfile: useBankSpecificProfile || false,
        }
      );
    },
    generateApplicationFromBankProfile: async (
      userId: string,
      bankId: string
    ): Promise<{ url: string }> => {
      return await axiosClient.post(
        "/panda/admin/generate-application-for-user",
        {
          userId,
          bankId,
          useBankSpecificProfile: true,
        }
      );
    },
    generateApplicationForUserFromVersion: async (
      userId: string,
      bankId: string,
      versionId: string
    ): Promise<{ url: string; versionId: string }> => {
      return await axiosClient.post(
        "/panda/admin/generate-application-for-user-from-version",
        { userId, bankId, versionId }
      );
    },
    generateApplicationForCompany: async (
      companyId: string,
      userIds: string[],
      bankId: string
    ): Promise<{
      url: string;
      applicationId: string;
      recipients: number;
      companyName: string;
      users: Array<{ id: string; name: string; email: string }>;
    }> => {
      return await axiosClient.post(
        "/panda/admin/generate-application-for-company",
        { companyId, userIds, bankId }
      );
    },
    generateInvoice: async (
      vehicleIds: string[],
      inventoryType?: string,
      recipient?: {
        email: string;
        first_name: string;
        last_name: string;
        company?: string;
        street_address?: string;
        city?: string;
        state?: string;
        postal_code?: string;
      },
      templateName?: string,
      bankProfileId?: string,
      leadId?: string,
      leadName?: string,
      isCashDeal?: boolean,
      shippingPrices?: Record<string, number>,
      lineItems?: Array<{ label: string; amount: number; type?: 'charge' | 'deposit' }>
    ): Promise<{
      url: string;
      documentId: string;
      invoiceId: string;
      invoiceNumber: string;
      status: string;
      vehicleCount: number;
      message: string;
    }> => {
      return await axiosClient.post("/panda/admin/generate-invoice", {
        vehicleIds,
        inventoryType,
        recipient,
        templateName,
        bankProfileId,
        leadId,
        leadName,
        isCashDeal,
        shippingPrices,
        lineItems,
      });
    },
    sendInvoice: async (invoiceId: string): Promise<{ status: string }> => {
      return await axiosClient.post("/panda/admin/send-invoice", {
        invoiceId,
      });
    },
    generatePayoffAuthorization: async (
      vehicleIds: string[],
      inventoryType?: string,
      recipient?: {
        email: string;
        first_name: string;
        last_name: string;
        company?: string;
        street_address?: string;
        city?: string;
        state?: string;
        postal_code?: string;
      },
      bankProfileId?: string,
      purchaseOrderId?: string
    ): Promise<{
      url: string;
      documentId: string;
      invoiceId: string;
      invoiceNumber: string;
      status: string;
      vehicleCount: number;
      message: string;
    }> => {
      return await axiosClient.post("/panda/admin/generate-payoff-authorization", {
        vehicleIds,
        inventoryType,
        recipient,
        bankProfileId,
        purchaseOrderId,
      });
    },
    savePdfInvoice: async (
      vehicleIds: string[],
      inventoryType: string,
      pdfUrl: string,
      recipient?: {
        email: string;
        first_name: string;
        last_name: string;
      },
      bankProfileId?: string,
      leadId?: string,
      leadName?: string,
      invoiceNumber?: string,
      isCashDeal?: boolean,
      client?: {
        email: string;
        first_name: string;
        last_name: string;
      },
      shippingPrices?: Record<string, number>
    ): Promise<{
      invoiceId: string;
      invoiceNumber: string;
      status: string;
      vehicleCount: number;
      message: string;
    }> => {
      return await axiosClient.post("/panda/admin/save-pdf-invoice", {
        vehicleIds,
        inventoryType,
        pdfUrl,
        recipient,
        bankProfileId,
        leadId,
        leadName,
        invoiceNumber,
        isCashDeal,
        client,
        shippingPrices,
      });
    },
    generateOdometerStatement: async (
      vehicleId: string,
      inventoryType?: string,
      recipient?: {
        email: string;
        first_name: string;
        last_name: string;
      },
      leadId?: string,
      leadName?: string
    ): Promise<{
      url: string;
      documentId: string;
      invoiceId: string;
      invoiceNumber: string;
      status: string;
      message: string;
    }> => {
      return await axiosClient.post("/panda/admin/generate-odometer-statement", {
        vehicleId,
        inventoryType,
        recipient,
        leadId,
        leadName,
      });
    },
  },
};
